/**
 * Merge-field rendering for content_assets bodies.
 *
 * WHY A VISIBLE MARKER IS NON-NEGOTIABLE
 * ---------------------------------------
 * `{{field}}` silently becoming "" is how "Hi , thanks for stopping by" gets
 * sent to a customer. Blank substitution passes every naive check -- the
 * string is well formed, the send succeeds -- and is wrong in a way nobody
 * notices until the customer does.
 *
 * So a missing key, a null, an undefined, or an empty/whitespace-only string
 * are all treated the same: none of them is an actual value, and all of them
 * render the literal marker `[[field?]]` instead of a blank. A visible,
 * ugly, unmissable marker in a preview is always better than a plausible
 * looking blank in a sent card.
 */

const MERGE_FIELD = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/**
 * One field's value, or the visible marker.
 *
 * Shared by renderTemplate() and renderSegments() so the marker rule cannot
 * drift between what a preview shows and what a send produces.
 */
function resolve(field: string, values: Record<string, unknown>): string {
  const value = values[field];
  if (value === null || value === undefined) return `[[${field}?]]`;
  const asString = String(value);
  if (asString.trim() === "") return `[[${field}?]]`;
  return asString;
}

export function renderTemplate(
  body: string,
  values: Record<string, unknown>,
): string {
  return body.replace(MERGE_FIELD, (_match, field: string) =>
    resolve(field, values),
  );
}

/* ── seeing the merge, and editing through it ──────────────────────────── */

/**
 * A template split into the parts that are literal and the parts that came
 * from a merge field.
 *
 * `field` is null for literal text and the field name for a merged run, so a
 * caller can color one and not the other without re-parsing anything.
 */
export type Segment = { text: string; field: string | null };

/**
 * The same substitution renderTemplate() performs, reported piece by piece.
 *
 * SEGMENTS RATHER THAN A SECOND REGEX PASS OVER THE OUTPUT. Once a template
 * is rendered, "Daniel" is indistinguishable from the word Daniel typed by
 * hand, and a screen that highlighted the merges by searching the output for
 * the values would color any sentence that happened to mention the company's
 * own name. The only moment the difference is knowable for certain is during
 * the substitution, so that is where it is recorded.
 *
 * Adjacent literal runs are not merged into one: a caller rendering these in
 * order produces exactly renderTemplate()'s string, and nothing here needs to
 * know how they are drawn.
 */
export function renderSegments(
  body: string,
  values: Record<string, unknown>,
): Segment[] {
  const segments: Segment[] = [];
  let cursor = 0;

  /*
    ITS OWN REGEX INSTANCE, NOT THE SHARED MERGE_FIELD.

    A /g/ regex carries `lastIndex` as mutable state on the object itself, and
    String.replace() RESETS it to 0 when it finishes. So walking the shared
    regex with exec() while calling renderTemplate() inside the loop -- which
    is what this function did first -- rewinds the scan on every iteration and
    never terminates. The test worker was killed rather than failing, which is
    how it announced itself.

    A fresh instance per call cannot be rewound by anybody else, and the value
    now comes from resolve() rather than from a nested render.
  */
  const scan = new RegExp(MERGE_FIELD.source, "g");
  let match: RegExpExecArray | null;
  while ((match = scan.exec(body)) !== null) {
    if (match.index > cursor)
      segments.push({ text: body.slice(cursor, match.index), field: null });
    segments.push({ text: resolve(match[1], values), field: match[1] });
    cursor = match.index + match[0].length;
  }
  if (cursor < body.length)
    segments.push({ text: body.slice(cursor), field: null });

  return segments;
}

/** One merged value, found in text somebody may since have typed over. */
export type MergeHit = { field: string; start: number; end: number };

/**
 * Find each merged value inside already-merged text, in order.
 *
 * THE EDITOR EDITS THE MERGED TEXT, WHICH IS WHAT MAKES THIS NECESSARY.
 * You see "Daniel" and you type over it; the stored override still has to be
 * a template, or the step silently stops merging and a later correction to
 * the contact's name never reaches it. So the merges are tracked by position
 * while you type and written back as `{{field}}` on save.
 *
 * SCANNING FORWARD, ONE MERGE AT A TIME, is deliberate. The values are
 * searched for in the order the template produced them and each search starts
 * after the previous match, so a body that mentions the company name in its
 * prose cannot steal the highlight belonging to a `{{company}}` further down.
 *
 * A MERGE THAT IS NOT FOUND IS SIMPLY GONE, and that is the correct outcome:
 * it means somebody typed over it, and what they typed is now literal text.
 * The highlight disappearing as they edit is the feedback that says so.
 */
export function locateMerges(
  text: string,
  expected: readonly { field: string; value: string }[],
): MergeHit[] {
  const hits: MergeHit[] = [];
  let cursor = 0;
  for (const { field, value } of expected) {
    if (value === "") continue;
    const at = text.indexOf(value, cursor);
    if (at === -1) continue;
    hits.push({ field, start: at, end: at + value.length });
    cursor = at + value.length;
  }
  return hits;
}

/**
 * Merged text back into a template, using the merges still standing.
 *
 * The inverse of renderSegments() for the runs locateMerges() could still
 * find. Everything else is left exactly as typed, which is the whole point:
 * an edited merge is meant to become literal.
 */
export function toTemplate(text: string, hits: readonly MergeHit[]): string {
  let out = "";
  let cursor = 0;
  for (const hit of hits) {
    // Defensive: a caller passing overlapping or unsorted hits would
    // otherwise produce silently scrambled text rather than an error.
    if (hit.start < cursor) continue;
    out += text.slice(cursor, hit.start) + `{{${hit.field}}}`;
    cursor = hit.end;
  }
  return out + text.slice(cursor);
}

/**
 * The values a template will merge, in the order they appear.
 *
 * Paired with locateMerges(): renderSegments() says what the merges rendered
 * to, and this reduces that to the list the editor tracks while typing.
 */
export function mergeValues(
  body: string,
  values: Record<string, unknown>,
): { field: string; value: string }[] {
  return renderSegments(body, values)
    .filter((s): s is Segment & { field: string } => s.field !== null)
    .map((s) => ({ field: s.field, value: s.text }));
}

/**
 * The rendered text with every merged run handed to `mark`.
 *
 * The wrapper is a parameter so this file stays free of React and of any
 * opinion about how a merge should look -- StepContentPanel wraps them in a
 * span, and a plain-text caller could wrap them in nothing at all.
 */
export function markMerges(
  body: string,
  values: Record<string, unknown>,
  mark: (value: string, field: string) => string,
): string {
  return renderSegments(body, values)
    .map((s) => (s.field === null ? s.text : mark(s.text, s.field)))
    .join("");
}
